import { data, useActionData } from 'react-router'
import {
	BookDemoTabbedForm,
	GridBackdrop,
} from '#app/components/book-demo-tabbed-form.tsx'
import { MarketingPageShell } from '#app/components/marketing-page-shell.tsx'
import { Reveal } from '#app/components/reveal.tsx'
import {
	createWebPageSchema,
	organizationSchema,
	webSiteSchema,
} from '#app/utils/constants.ts'
import { processSalesLeadSubmission } from '#app/utils/marketing-form.server.ts'
import { type Route } from './+types/limited-time-offer.ts'

const MetaTitle = 'Limited Time Offer | Two Months Free | DealerProEdge'
const MetaDescription =
	'Get two months free with no contract from DealerProEdge. Built for dealers with 50+ units who want room to evaluate performance, visibility, lead flow, and a better website path.'
const directScheduleNamespace = 'limited-time-offer-direct'

export const handle = { chrome: 'payload' }

const transitionOffer = [
	{
		title: 'Month one',
		description:
			'We get your dealership running on DealerProEdge instead of your current website provider, then share the Google Performance Audit to identify the speed, SEO, and lead-path gaps that matter most.',
	},
	{
		title: 'Month two',
		description:
			'We review the performance data with you so your team can decide whether to unwind your current vendor agreement, without paying two website providers at once.',
	},
]

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/limited-time-offer'
	const canonicalUrl = `${origin}${path}`
	const pageSchema = createWebPageSchema({
		name: MetaTitle,
		description: MetaDescription,
		url: canonicalUrl,
	})

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'twitter:title', content: MetaTitle },
		{ name: 'twitter:description', content: MetaDescription },
		{ property: 'og:title', content: MetaTitle },
		{ property: 'og:description', content: MetaDescription },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonicalUrl },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
		{ 'script:ld+json': organizationSchema },
		{ 'script:ld+json': webSiteSchema },
		{ 'script:ld+json': pageSchema },
	]
}

export async function action(args: Route.ActionArgs) {
	const formData = await args.request.formData()
	const result = await processSalesLeadSubmission(
		formData,
		'limited-time-offer',
		args,
	)

	if (!result.success) {
		return data(
			{ result: result.submission },
			{
				status: result.status,
				headers: 'headers' in result ? result.headers : undefined,
			},
		)
	}

	return data({ success: true })
}

export default function LimitedTimeOfferPage() {
	const actionData = useActionData<typeof action>()
	const didSubmit = actionData && 'success' in actionData && actionData.success
	const lastResult =
		actionData && 'result' in actionData ? actionData.result : undefined

	return (
		<MarketingPageShell>
			<div className="bg-[#f7f7f2] text-[#101010]">
				<section className="relative overflow-hidden border-b border-black/10">
					<GridBackdrop />
					<div className="mx-auto grid min-h-[calc(100vh-150px)] max-w-[1440px] grid-cols-1 px-5 pb-14 pt-8 sm:px-8 sm:pt-10 lg:grid-cols-[0.95fr_1.05fr] lg:px-10 lg:pb-20 lg:pt-12">
						<Reveal className="relative flex flex-col justify-start border-black/10 pb-10 lg:border-r lg:pr-14">
							<div className="flex w-full max-w-[520px] flex-col border border-black bg-[#fffffa] text-center text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-black sm:inline-flex sm:w-fit sm:max-w-none sm:flex-row sm:text-xs">
								<span className="border-b border-black bg-[#EC1C23] px-3 py-2 text-white sm:border-b-0 sm:border-r">
									Limited Time Offer
								</span>
								<span className="px-3 py-2">Dealers with 50+ units</span>
							</div>

							<h1 className="mt-8 max-w-[720px] text-[clamp(2.35rem,4.8vw,4.55rem)] font-medium leading-[1.01] text-[#101010]">
								Two months free. No contract required.
							</h1>

							<p className="text-black/68 mt-7 max-w-[620px] text-base leading-7 md:text-lg md:leading-8">
								Use the two free months to see what improves, understand the
								difference, and decide if DealerProEdge has earned the switch.
							</p>

							<div className="mt-10 grid max-w-2xl grid-cols-1 border border-black/10 bg-[#fffffa]/60 md:grid-cols-2">
								{transitionOffer.map((item) => (
									<div
										key={item.title}
										className="border-black/10 p-6 md:p-7 max-md:[&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-r"
									>
										<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#EC1C23]">
											{item.title}
										</p>
										<p className="mt-4 text-base leading-7 text-black/70">
											{item.description}
										</p>
									</div>
								))}
							</div>
						</Reveal>

						<Reveal
							delay={0.08}
							className="relative flex items-start pt-10 lg:pl-14 lg:pt-0"
						>
							<div className="w-full">
								<BookDemoTabbedForm
									isSubmitted={Boolean(didSubmit)}
									lastResult={lastResult}
									scheduleNamespace={directScheduleNamespace}
									successDescription="Your details went to the DealerProEdge team. We will follow up about the two-month offer and include the Google Performance Audit as part of the review."
									usePairedFieldRows
								/>
							</div>
						</Reveal>
					</div>
				</section>
			</div>
		</MarketingPageShell>
	)
}
